import { isAxiosError } from 'axios';
import cloneDeep from 'lodash-es/cloneDeep';
import { storeToRefs } from 'pinia';
import { ActionContext } from 'vuex';

import { PAYMENT_STRATEGIES } from '@/constants';
import { CartCheckout, CartItem, CartOrder, CartOrderDetails } from '@/models/cart';
import { OrderPromoCodeDelete } from '@/models/order';
import { RootState } from '@/models/vuex/rootState';
import notify from '@/notify';
import { useUserStore } from '@/piniaStores/user/user.store';
import i18n from '@/plugins/i18n';
import api from '@/services/api';
import { track } from '@/track';

import { CartState } from './state';

/**
 * Fetch Cart orders
 */
export async function fetchCartCount({ commit, state, dispatch }: ActionContext<CartState, RootState>) {
  commit('SET_CART_COUNT_OVERRIDE', state.cartItemCount);
  const cartStats = await api.cart.getCartStats();

  commit('SET_CART_COUNT', cartStats);
  commit('SET_CART_COUNT_OVERRIDE', null);

  if (state.isLoaded) {
    dispatch('fetchCart');
  }

  return true;
}

/**
 * Fetch Cart orders
 */
export async function fetchCart({ commit, getters }: ActionContext<CartState, RootState>) {
  commit('SET_IS_LOADING', true);

  const promoCodes = getters.promoCodes;
  const cart = await api.cart.getCart(promoCodes);

  cart.orders.forEach((order) => {
    order.customerDiscountPercent = parseFloat(order.customerDiscountPercent);
    order.items.forEach((item) => {
      item.quantity = parseFloat(item.quantity);
      item.orderId = order.id;

      if (item.reservedAt) {
        item.reservedAt = Date.parse(item.reservedAt);
      }
    });
  });

  commit('SET_CART', cart);
  commit('SET_IS_LOADING', false);
  commit('SET_IS_LOADED', true);

  return true;
}

/**
 * Checkout entire cart or just a single buyer's cart
 * If no buyerId is provided the entire cart will be checked out
 * @param {object} payload the payload object
 * @param {number} [payload.buyerId] the id of the buyer's cart to be checked out.
 * @param {number} [payload.sellerId] id of the buyer->seller cart to be cleared.
 * @param {boolean} payload.isWarehouseOrder if the order(s) should be restocked to the warehouse or not
 */
export async function checkoutCart({ commit, state }: ActionContext<CartState, RootState>, payload: CartCheckout) {
  const { buyerId, sellerId, isWarehouseOrder } = payload || {};
  let { orders } = state;

  if (buyerId) {
    orders = orders.filter((order) => order.buyer.id === buyerId);

    if (sellerId) {
      orders = orders.filter((order) => order.seller.id === sellerId);
    }
  }

  // Grab the details for all the relevant orders
  const orderDetailsPayload = orders.map((order) => {
    const orderDetails: CartOrderDetails = {
      orderId: order.id,
      isWarehouseOrder,
    };

    const details = state.orderDetails[order.id];

    if (details) {
      orderDetails.notes = details.notes;
      orderDetails.deliveryPreferences = details.deliveryPreferences;
      orderDetails.llfPaymentMethod = details.llfPaymentMethod;

      if (details.selectedPaymentOption?.isPaymentOnSellThrough) {
        const paymentOption = cloneDeep(details.selectedPaymentOption);
        delete paymentOption.isPaymentOnSellThrough;
        orderDetails.selectedPaymentOption = paymentOption;
        orderDetails.isPaymentOnSellThrough = true;

        if (paymentOption.endTermOption) {
          orderDetails.paymentOnSellThroughSettings = {
            endTermOption: paymentOption.endTermOption,
            endTermDiscount: null,
          };

          if (paymentOption.endTermOption === 'pay_balance') {
            orderDetails.paymentOnSellThroughSettings.endTermDiscount = paymentOption.endTermDiscount;
          }
        }

        delete paymentOption.endTermOption;
        delete paymentOption.endTermDiscount;
      } else {
        orderDetails.selectedPaymentOption = details.selectedPaymentOption;
      }
    }

    if (order.promoCode) {
      orderDetails.promoCodeId = order.promoCode.id;
    }

    return orderDetails;
  });

  const response = await api.cart.checkoutCart({
    cartInfo: payload,
    orderDetails: orderDetailsPayload,
  });

  trackSubmittedOrders(orders, orderDetailsPayload);

  commit('SET_ORDERS', response);

  return orders;
}

function trackSubmittedOrders(orders, orderDetails) {
  // Check for orders that opt-in to Reverse Factoring on submission.
  orders.forEach((order) => {
    const details = orderDetails.find((detail) => detail.orderId === order.id);

    if (details?.selectedPaymentOption) {
      const paymentStrategy = details.selectedPaymentOption.paymentStrategy;
      const isReverseFactorOptIn =
        paymentStrategy === PAYMENT_STRATEGIES.REVERSE_FACTORED && details.selectedPaymentOption.isOptIn;

      if (isReverseFactorOptIn) {
        track('Reverse Factor Opt-In Submitted', {
          buyer_id: order.buyer.id,
          group_id: order.buyer.groupId,
          buyer_flex_on: order.buyer.enablePaymentsFlexible,
          buyer_direct_on: order.buyer.enablePaymentsDirect,
          buyer_name: order.buyer.name,
          seller_id: order.seller.id,
          seller_flex_on: order.seller.enablePaymentsFlexible,
          seller_direct_on: order.seller.enablePaymentsDirect,
          seller_name: order.seller.name,
        });
      }
    }
  });
}

/**
 * Clear cart
 * @param {number} buyerId id of the buyer cart to be cleared.  If null clear entire cart
 * @param {number} sellerId id of the buyer->seller cart to be cleared.
 */
export async function deleteCart({ state, dispatch }: ActionContext<CartState, RootState>, payload: CartCheckout) {
  const { buyerId, sellerId } = payload;

  await api.cart.deleteCart({ buyerId, sellerId });

  let orders = state.orders;

  if (buyerId) {
    orders = state.orders.filter((order) => order.buyer.id === buyerId);

    if (sellerId) {
      orders = orders.filter((order) => order.seller.id === sellerId);
    }
  }

  // eslint-disable-next-line require-await
  orders.forEach(async (order) => {
    dispatch('removeOrder', order.id);
  });
}

/**
 * Update order detail
 */
export function updateOrderDetail(
  { commit, state }: ActionContext<CartState, RootState>,
  payload: {
    detail: string;
    applyToAllGroupedOrders?: boolean;
    orderId?: string;
    order?: CartOrder;
    value: string;
  },
) {
  const { applyToAllGroupedOrders = false, detail, order, orderId, value } = payload;
  // if an order with order.groupedOrders and applyToAllGroupedOrders are provided, update each order
  // with the order detail, otherwise use the order.id or orderId
  const orderIds = order
    ? order.groupedOrders && applyToAllGroupedOrders
      ? order.groupedOrders.map((order) => order.id)
      : [order.id]
    : [orderId];

  orderIds.forEach((_orderId) => {
    if (!_orderId) {
      return;
    }

    const ordersDetailsIndex = cloneDeep(state.orderDetails) || {};

    const order = ordersDetailsIndex[_orderId] || {};
    const newDetails = { [detail]: value };

    ordersDetailsIndex[_orderId] = { ...order, ...newDetails };

    commit('SET_ORDER_DETAILS', ordersDetailsIndex);

    const { setUserSettingsByPage } = useUserStore();

    setUserSettingsByPage({ name: 'order-details', data: ordersDetailsIndex });
  });
}

/**
 * Load any saved order details for an order
 */
export function fetchCachedOrderDetails({ commit }: ActionContext<CartState, RootState>) {
  // Retrieve any cached order details
  const { getUserSettingsByPage } = storeToRefs(useUserStore());

  const orderDetailsIndex = getUserSettingsByPage.value('order-details');

  if (orderDetailsIndex) {
    commit('SET_ORDER_DETAILS', orderDetailsIndex);
  }
}

/**
 * Remove any saved order details for an order
 */
export function removeOrder({ commit, state }: ActionContext<CartState, RootState>, orderId: string) {
  commit('REMOVE_ORDER', orderId);
  commit('REMOVE_ORDER_DETAILS', orderId);

  const orderDetails = cloneDeep(state.orderDetails);

  const { setUserSettingsByPage } = useUserStore();

  setUserSettingsByPage({
    name: 'order-details',
    data: orderDetails,
  });
}

/**
 * Fetch Companies
 */
export async function fetchCompanies({ commit }: ActionContext<CartState, RootState>) {
  const response = await api.company.getCompanies();

  commit('SET_COMPANIES', response.results);
}

/**
 * Load cart
 */
export async function fetchMiniCart({ dispatch }: ActionContext<CartState, RootState>) {
  await Promise.all([dispatch('fetchCompanies'), dispatch('fetchCartCount')]);
}

/**
 * Update cart item
 */
export async function updateCartItem({ commit, dispatch }: ActionContext<CartState, RootState>, item: CartItem) {
  commit('SET_IS_UPDATING_CART', true);

  try {
    await api.cart.updateItem(item);
    await dispatch('fetchCartCount');
  } catch (error) {
    if (isAxiosError(error)) {
      const { response } = error;

      commit('UPDATE_ITEM', {
        ...item,
        updateValidationErrors: response?.data || [],
      });

      // In case the item is removed from another cart instance
      if (response?.status === 404) {
        await dispatch('fetchCartCount');
      }

      return;
    }

    throw error;
  } finally {
    commit('SET_IS_UPDATING_CART', false);
  }
}

/**
 * Clear errors on a cart item.
 */
export function clearCartItemError({ commit }: ActionContext<CartState, RootState>, item: CartItem) {
  commit('UPDATE_ITEM', {
    ...item,
    updateValidationErrors: [],
  });
}

/**
 * Check if a promo code is valid and if it is add it to the state and reload the cart
 */
export async function addPromoCode(
  { dispatch }: ActionContext<CartState, RootState>,
  payload: { code: string; buyerId: number; brandId: number; orderId: string },
) {
  const promoCode = await api.cart.validatePromoCode(payload.code, payload.buyerId, payload.brandId, payload.orderId);

  dispatch('updateOrderDetail', {
    orderId: payload.orderId,
    detail: 'promoCodeId',
    value: promoCode.id,
  });

  await dispatch('fetchCartCount');
}

/**
 * Remove Promo Code
 */
export async function removePromoCode(
  { dispatch }: ActionContext<CartState, RootState>,
  payload: OrderPromoCodeDelete,
) {
  await api.order.removePromoCodeFromCart(payload);
  dispatch('updateOrderDetail', {
    orderId: payload.orderId,
    detail: 'promoCodeId',
    value: '',
  });

  await dispatch('fetchCartCount');

  if (!payload.silent) {
    notify.success(i18n.t('cart.promoCodeRemoved'));
  }
}

/**
 * Add a single product to the cart
 * @param {CartItem} cartItem
 */
// eslint-disable-next-line require-await
export async function addToCart(_obj, cartItem: CartItem) {
  return api.cart.addToCart(cartItem);
}

/**
 * Add multiple products to the cart at once
 * @param {CartItem[]} cartItems items to be added to the cart
 */
// eslint-disable-next-line require-await
export async function bulkAddToCart(_obj, cartItems: CartItem[]) {
  return api.cart.bulkAddToCart(cartItems);
}

export function setIsUpdatingCart({ commit }: ActionContext<CartState, RootState>, status: boolean) {
  commit('SET_IS_UPDATING_CART', status);
}
